import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Trash2, Music, Edit, Save, X, Eye, ChevronUp, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ExerciseCard({ exercise, lessonId, lessonIndex, exIndex, isEven, onUpdate, onDelete }) {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editingAudioLabels, setEditingAudioLabels] = useState(false);
  const [audioLabels, setAudioLabels] = useState(exercise.audio_labels || []);
  const [editData, setEditData] = useState({
    title: exercise.title,
    description: exercise.description,
    text_content: exercise.text_content || ''
  });
  
  const bgColor = isEven ? 'bg-green-50' : 'bg-yellow-50';

  const handleSave = async () => {
    try {
      await axios.patch(`${API}/exercises/${exercise.id}`, editData);
      toast.success('Exercise updated!');
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to update exercise');
    }
  };

  const handleAudioUpload = async (files) => {
    if (!files || files.length === 0) return;

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    try {
      toast.loading('Uploading audio files...');
      await axios.post(`${API}/exercises/${exercise.id}/upload-audio`, formData);
      toast.dismiss();
      toast.success(`Uploaded ${files.length} audio file(s)!`);
      onUpdate();
    } catch (error) {
      console.error('Error:', error);
      toast.dismiss();
      toast.error('Failed to upload audio');
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      toast.loading('Uploading image...');
      await axios.post(`${API}/exercises/${exercise.id}/upload-image`, formData);
      toast.dismiss();
      toast.success('Image uploaded!');
      onUpdate();
    } catch (error) {
      console.error('Error:', error);
      toast.dismiss();
      toast.error('Failed to upload image');
    }
  };

  const deleteImage = async (filename) => {
    try {
      await axios.delete(`${API}/exercises/${exercise.id}/images/${filename}`);
      toast.success('Image deleted');
      onUpdate();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to delete image');
    }
  };

  const deleteAudio = async (filename) => {
    try {
      await axios.delete(`${API}/exercises/${exercise.id}/audio/${filename}`);
      toast.success('Audio deleted');
      onUpdate();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to delete audio');
    }
  };

  const startEditingLabels = () => {
    // Initialize labels array to match audio files count
    const currentLabels = exercise.audio_labels || [];
    const numAudioFiles = exercise.audio_files?.length || 0;
    const initializedLabels = [];
    
    for (let i = 0; i < numAudioFiles; i++) {
      initializedLabels.push(currentLabels[i] || `Audio ${i + 1}`);
    }
    
    setAudioLabels(initializedLabels);
    setEditingAudioLabels(true);
  };

  const saveAudioLabels = async () => {
    try {
      await axios.patch(`${API}/exercises/${exercise.id}/audio-labels`, {
        audio_labels: audioLabels
      });
      toast.success('Audio labels updated!');
      setEditingAudioLabels(false);
      onUpdate();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to update audio labels');
    }
  };

  const updateLabel = (index, value) => {
    const newLabels = [...audioLabels];
    newLabels[index] = value;
    setAudioLabels(newLabels);
  };

  const moveAudioUp = async (index) => {
    if (index === 0) return; // Already at top

    const newAudioFiles = [...exercise.audio_files];
    const newAudioLabels = [...(exercise.audio_labels || [])];

    // Swap audio files
    [newAudioFiles[index], newAudioFiles[index - 1]] = [newAudioFiles[index - 1], newAudioFiles[index]];
    
    // Swap labels if they exist
    if (newAudioLabels.length > 0) {
      [newAudioLabels[index], newAudioLabels[index - 1]] = [newAudioLabels[index - 1], newAudioLabels[index]];
    }

    try {
      await axios.patch(`${API}/exercises/${exercise.id}/reorder-audio`, {
        audio_files: newAudioFiles,
        audio_labels: newAudioLabels
      });
      toast.success('Audio file moved up');
      onUpdate();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to reorder audio');
    }
  };

  const moveAudioDown = async (index) => {
    if (index === exercise.audio_files.length - 1) return; // Already at bottom

    const newAudioFiles = [...exercise.audio_files];
    const newAudioLabels = [...(exercise.audio_labels || [])];

    // Swap audio files
    [newAudioFiles[index], newAudioFiles[index + 1]] = [newAudioFiles[index + 1], newAudioFiles[index]];
    
    // Swap labels if they exist
    if (newAudioLabels.length > 0) {
      [newAudioLabels[index], newAudioLabels[index + 1]] = [newAudioLabels[index + 1], newAudioLabels[index]];
    }

    try {
      await axios.patch(`${API}/exercises/${exercise.id}/reorder-audio`, {
        audio_files: newAudioFiles,
        audio_labels: newAudioLabels
      });
      toast.success('Audio file moved down');
      onUpdate();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to reorder audio');
    }
  };

  return (
    <Card className={bgColor} data-testid={`exercise-item-${lessonIndex}-${exIndex}`}>
      <CardContent className="pt-4">
        <div className="space-y-3">
          {/* Header with Edit/Delete */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {!isEditing ? (
                <>
                  <h4 className="font-medium" data-testid={`exercise-title-${lessonIndex}-${exIndex}`}>
                    {exercise.title}
                  </h4>
                  <p className="text-sm text-gray-600 mt-1" data-testid={`exercise-desc-${lessonIndex}-${exIndex}`}>
                    {exercise.description}
                  </p>
                  {exercise.text_content && (
                    <p className="text-xs text-gray-400 mt-1">Has practice text</p>
                  )}
                </>
              ) : (
                <div className="space-y-2">
                  <Input
                    placeholder="Exercise title"
                    value={editData.title}
                    onChange={(e) => setEditData({...editData, title: e.target.value})}
                    data-testid={`edit-title-${lessonIndex}-${exIndex}`}
                  />
                  <Textarea
                    placeholder="Exercise description"
                    value={editData.description}
                    onChange={(e) => setEditData({...editData, description: e.target.value})}
                    rows={2}
                    data-testid={`edit-desc-${lessonIndex}-${exIndex}`}
                  />
                  <div>
                    <Label className="text-xs mb-1 block">Practice Text Formatting</Label>
                    <Textarea
                      placeholder="Enter practice text... See formatting tips below"
                      value={editData.text_content}
                      onChange={(e) => setEditData({...editData, text_content: e.target.value})}
                      rows={6}
                      className="font-mono text-sm"
                      data-testid={`edit-text-${lessonIndex}-${exIndex}`}
                    />
                    <div className="text-xs text-gray-500 mt-1 space-y-1">
                      <p className="font-semibold">Formatting options:</p>
                      <p>• <span className="font-mono bg-gray-100 px-1">Column 1 | Column 2 | Column 3</span> - Creates table/spreadsheet (each line = row)</p>
                      <p>• <span className="font-mono bg-gray-100 px-1">|||</span> - Splits text into side-by-side columns</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-1">
              {!isEditing ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/exercise/${exercise.id}`)}
                    title="Preview Exercise"
                    data-testid={`preview-exercise-btn-${lessonIndex}-${exIndex}`}
                  >
                    <Eye className="w-4 h-4 text-purple-500" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                    data-testid={`edit-exercise-btn-${lessonIndex}-${exIndex}`}
                  >
                    <Edit className="w-4 h-4 text-blue-500" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onDelete}
                    data-testid={`delete-exercise-btn-${lessonIndex}-${exIndex}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSave}
                    data-testid={`save-exercise-btn-${lessonIndex}-${exIndex}`}
                  >
                    <Save className="w-4 h-4 text-green-500" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setIsEditing(false);
                      setEditData({
                        title: exercise.title,
                        description: exercise.description,
                        text_content: exercise.text_content || ''
                      });
                    }}
                    data-testid={`cancel-edit-btn-${lessonIndex}-${exIndex}`}
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </Button>
                </>
              )}
            </div>
          </div>

          {!isEditing && (
            <>
              {/* Audio Upload - Multiple Files */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">Audio Files (upload multiple)</Label>
                  {exercise.audio_files && exercise.audio_files.length > 0 && !editingAudioLabels && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs"
                      onClick={startEditingLabels}
                      data-testid={`edit-labels-btn-${lessonIndex}-${exIndex}`}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit Labels
                    </Button>
                  )}
                </div>
                <div className="space-y-2 mt-1">
                  <Input
                    type="file"
                    accept=".mp3,.wav,.m4a"
                    multiple
                    onChange={(e) => handleAudioUpload(e.target.files)}
                    className="text-sm"
                    data-testid={`audio-upload-${lessonIndex}-${exIndex}`}
                  />
                  {/* Display uploaded audio files */}
                  {exercise.audio_files && exercise.audio_files.length > 0 && (
                    <div className="space-y-1">
                      {editingAudioLabels ? (
                        <>
                          {exercise.audio_files.map((filename, audioIndex) => (
                            <div key={audioIndex} className="flex items-center gap-2 bg-white p-2 rounded text-xs">
                              <Music className="w-3 h-3 text-blue-600" />
                              <Input
                                value={audioLabels[audioIndex] || ''}
                                onChange={(e) => updateLabel(audioIndex, e.target.value)}
                                placeholder={`Label for audio ${audioIndex + 1}`}
                                className="h-7 text-xs"
                                data-testid={`label-input-${lessonIndex}-${exIndex}-${audioIndex}`}
                              />
                            </div>
                          ))}
                          <div className="flex gap-2 pt-1">
                            <Button
                              size="sm"
                              onClick={saveAudioLabels}
                              className="text-xs h-7"
                              data-testid={`save-labels-btn-${lessonIndex}-${exIndex}`}
                            >
                              <Save className="w-3 h-3 mr-1" />
                              Save Labels
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingAudioLabels(false)}
                              className="text-xs h-7"
                              data-testid={`cancel-labels-btn-${lessonIndex}-${exIndex}`}
                            >
                              <X className="w-3 h-3 mr-1" />
                              Cancel
                            </Button>
                          </div>
                        </>
                      ) : (
                        exercise.audio_files.map((filename, audioIndex) => {
                          const label = exercise.audio_labels && exercise.audio_labels[audioIndex]
                            ? exercise.audio_labels[audioIndex]
                            : `Audio ${audioIndex + 1}`;
                          return (
                            <div key={audioIndex} className="flex items-center gap-2 bg-white p-2 rounded text-xs">
                              {/* Order Controls */}
                              <div className="flex flex-col gap-0.5">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-4 w-4 p-0 hover:bg-gray-100"
                                  onClick={() => moveAudioUp(audioIndex)}
                                  disabled={audioIndex === 0}
                                  title="Move up"
                                  data-testid={`move-audio-up-${lessonIndex}-${exIndex}-${audioIndex}`}
                                >
                                  <ChevronUp className={`w-3 h-3 ${audioIndex === 0 ? 'text-gray-300' : 'text-gray-600'}`} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-4 w-4 p-0 hover:bg-gray-100"
                                  onClick={() => moveAudioDown(audioIndex)}
                                  disabled={audioIndex === exercise.audio_files.length - 1}
                                  title="Move down"
                                  data-testid={`move-audio-down-${lessonIndex}-${exIndex}-${audioIndex}`}
                                >
                                  <ChevronDown className={`w-3 h-3 ${audioIndex === exercise.audio_files.length - 1 ? 'text-gray-300' : 'text-gray-600'}`} />
                                </Button>
                              </div>
                              
                              {/* Audio Info */}
                              <div className="flex items-center gap-2 flex-1">
                                <Music className="w-3 h-3 text-blue-600" />
                                <span className="font-medium text-blue-600">{label}</span>
                                <span className="text-gray-400">•</span>
                                <span className="truncate max-w-[120px] text-gray-500">{filename}</span>
                              </div>
                              
                              {/* Delete Button */}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => deleteAudio(filename)}
                                data-testid={`delete-audio-btn-${lessonIndex}-${exIndex}-${audioIndex}`}
                              >
                                <Trash2 className="w-3 h-3 text-red-500" />
                              </Button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Image Upload */}
              <div>
                <Label className="text-xs">Add Image</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e.target.files[0])}
                  className="text-sm mt-1"
                  data-testid={`image-upload-${lessonIndex}-${exIndex}`}
                />
              </div>

              {/* Display Images */}
              {exercise.image_files && exercise.image_files.length > 0 && (
                <div className="grid grid-cols-4 gap-2">
                  {exercise.image_files.map((filename, imgIndex) => (
                    <div key={imgIndex} className="relative group">
                      <img
                        src={`${API}/files/image/${filename}`}
                        alt={`Exercise ${imgIndex + 1}`}
                        className="w-full h-20 object-cover rounded"
                        data-testid={`exercise-image-${lessonIndex}-${exIndex}-${imgIndex}`}
                      />
                      <Button
                        variant="destructive"
                        size="sm"
                        className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => deleteImage(filename)}
                        data-testid={`delete-image-btn-${lessonIndex}-${exIndex}-${imgIndex}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
